import { addToCart, openCartOrGo, goToCart } from '@theme/dert-cart-add';

/**
 * <s-quick-add> — one-tap add to cart for product cards. Adds the card's variant, opens the cart drawer
 * and briefly swaps the cart icon for a check mark. Falls back to a classic cart POST when the
 * storefront cart actions are unavailable.
 */
class SQuickAdd extends HTMLElement {
  #ac;
  #busy = false;

  connectedCallback() {
    this.#ac = new AbortController();
    this.querySelector('button')?.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.#add();
    }, { signal: this.#ac.signal });
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  async #add() {
    if (this.#busy) return;
    const button = this.querySelector('button');
    const variantId = this.dataset.variantId;
    if (!button || !variantId) return;

    this.#busy = true;
    button.setAttribute('aria-busy', 'true');
    button.classList.add('opacity-60', 'cursor-wait');

    try {
      const result = await addToCart([{ id: variantId, quantity: 1 }]);
      if (!result.ok) {
        // Could not add here: open the product page so the shopper can add it there.
        if (this.dataset.productUrl) window.location.href = this.dataset.productUrl;
        return;
      }
      if (!result.refreshed) {
        goToCart();
        return;
      }
      this.#showDone(button);
      await openCartOrGo();
    } catch (error) {
      console.warn('[quick-add]', error);
    } finally {
      this.#busy = false;
      button.removeAttribute('aria-busy');
      button.classList.remove('opacity-60', 'cursor-wait');
    }
  }

  #showDone(button) {
    const icon = this.querySelector('.dert-quick-add__icon');
    const done = this.querySelector('.dert-quick-add__done');
    if (!icon || !done) return;
    const label = button.getAttribute('aria-label');
    const text = this.querySelector('.dert-quick-pill__label');
    if (text) text.textContent = text.dataset.added || text.textContent;
    icon.classList.add('hidden');
    done.classList.remove('hidden');
    done.classList.add('inline-flex');
    button.setAttribute('aria-label', button.dataset.addedLabel || label);
    setTimeout(() => {
      icon.classList.remove('hidden');
      done.classList.add('hidden');
      done.classList.remove('inline-flex');
      button.setAttribute('aria-label', label);
      if (text) text.textContent = text.dataset.default || text.textContent;
    }, 1800);
  }
}

if (!customElements.get('s-quick-add')) customElements.define('s-quick-add', SQuickAdd);
